/**
 * Cadastrar no IW o paciente do Caso marcado como novo no IW (NR-258).
 *
 * O back monta o cadastro do que o Caso tem, enfileira e faz a primeira tentativa na hora: a resposta já traz o
 * desfecho e o detalhe atualizado. O ator NÃO vai no corpo: o back o lê da sessão.
 */

import { queryOptions } from '@tanstack/react-query';
import type {
  IwRegistrationPreviewResponse,
  RegisterIwPatientRequest,
  RegisterIwPatientResponse,
} from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { chavesDeCasos } from './casos';
import { mapearCasoDetalhado } from './mapeadores';
import type { CasoDetalhado } from './tipos';

/** `Desconhecido`: o back mandou um desfecho que esta tela ainda não conhece — aparece sinalizado, nunca some. */
export type DesfechoDoCadastro = 'Linked' | 'Pending' | 'Homonym' | 'Refused' | 'Unanswered' | 'Desconhecido';

export interface CampoRecusadoPeloIw {
  /** O nome do campo no IW (`idFilial`, `apelido`...). */
  readonly campo: string;
  readonly mensagem: string;
}

export interface ResultadoDoCadastroNoIw {
  readonly desfecho: DesfechoDoCadastro;
  readonly idPacienteNoIw: string | null;
  readonly camposRecusados: readonly CampoRecusadoPeloIw[];
  readonly motivo: string | null;
  /** A recusa é da plataforma (o Caso mudou, o vínculo não fechou...), não do IW: a tela não a atribui ao IW. */
  readonly recusadoPelaPlataforma: boolean;
  readonly caso: CasoDetalhado;
}

/** Os nomes de campo do IW, como a pessoa os reconhece no Caso. */
export const ROTULOS_DO_CAMPO_DO_IW: Readonly<Record<string, string>> = {
  nome: 'nome',
  apelido: 'apelido',
  dataNascimento: 'data de nascimento',
  idFilial: 'filial do IW',
  cpf: 'CPF',
  usuarioIw: 'usuário do IW de quem cadastrou',
};

const DESFECHOS: readonly string[] = ['Linked', 'Pending', 'Homonym', 'Refused', 'Unanswered'];

export function mapearResultadoDoCadastro(resposta: RegisterIwPatientResponse): ResultadoDoCadastroNoIw {
  const desfecho = resposta.outcome ?? '';
  return {
    desfecho: DESFECHOS.includes(desfecho) ? (desfecho as DesfechoDoCadastro) : 'Desconhecido',
    idPacienteNoIw: resposta.iwPatientId ?? null,
    // Campo sem nome não diz nada a quem lê: fica de fora. Sem mensagem, o nome sozinho ainda ajuda.
    camposRecusados: (resposta.refusedFields ?? []).flatMap((c): CampoRecusadoPeloIw[] =>
      c.field ? [{ campo: c.field, mensagem: c.message ?? '' }] : [],
    ),
    motivo: resposta.reason ?? null,
    recusadoPelaPlataforma: resposta.refusedByPlatform,
    caso: mapearCasoDetalhado(resposta.case),
  };
}

/** Uma filial ativa do IW. O IW não expõe os ids: a lista vem da configuração da plataforma. */
export interface FilialDoIw {
  readonly id: number;
  readonly nome: string;
}

/** O cadastro como iria ao IW, para o formulário abrir preenchido. Vazio (`''`/`null`) = o Caso não tem. */
export interface CadastroPrevisto {
  readonly nome: string;
  readonly apelido: string;
  /** AAAA-MM-DD, como o campo de data espera. */
  readonly nascimento: string;
  /** Só os dígitos. */
  readonly cpf: string;
  readonly filial: number | null;
  readonly filiais: readonly FilialDoIw[];
}

/** O que a pessoa confere ou corrige antes de cadastrar. O nome e o apelido não entram: vêm do e-mail. */
export interface DadosDoCadastro {
  readonly nascimento: string;
  readonly cpf: string;
  readonly filial: number | null;
}

export function mapearCadastroPrevisto(resposta: IwRegistrationPreviewResponse): CadastroPrevisto {
  return {
    nome: resposta.name ?? '',
    apelido: resposta.nickname ?? '',
    nascimento: resposta.birthDate ?? '',
    cpf: resposta.taxId ?? '',
    filial: resposta.branchId ?? null,
    filiais: (resposta.branches ?? []).map((f) => ({ id: f.id, nome: f.name ?? String(f.id) })),
  };
}

/** `GET /casos/{id}/vinculo-iw/cadastro`: lido a cada abertura do formulário, nada fica guardado depois. */
export function opcoesDoCadastroPrevisto(id: string, habilitado: boolean) {
  return queryOptions({
    queryKey: chavesDeCasos.cadastroPrevisto(id),
    queryFn: ({ signal }) =>
      requisitar<IwRegistrationPreviewResponse>(`/casos/${encodeURIComponent(id)}/vinculo-iw/cadastro`, {
        sinal: signal,
      }).then(mapearCadastroPrevisto),
    enabled: habilitado && id !== '',
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * `POST /casos/{id}/vinculo-iw/cadastrar`. Nascimento e CPF informados viram correção do e-mail do Caso; a filial vai
 * com o envio, e o apelido é sempre o sugerido a partir do nome. Caso que não está como novo no IW: `409`; falta dado: `422`; formato errado: `400`.
 */
export function cadastrarPacienteNoIw(
  id: string,
  dados?: DadosDoCadastro,
  sinal?: AbortSignal,
): Promise<ResultadoDoCadastroNoIw> {
  const corpo: RegisterIwPatientRequest | undefined = dados
    ? {
        birthDate: dados.nascimento || null,
        // Vazio não é "sem resposta": a pessoa apagou o CPF, e o cadastro vai sem ele.
        taxId: dados.cpf.trim(),
        branchId: dados.filial,
      }
    : undefined;
  return requisitar<RegisterIwPatientResponse>(`/casos/${encodeURIComponent(id)}/vinculo-iw/cadastrar`, {
    method: 'POST',
    corpo,
    sinal,
  }).then(mapearResultadoDoCadastro);
}
